<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4 shadow-sm-custom sticky top-0 z-10'
      ]"
      style="box-shadow: var(--shadow-sm)"
    >
      <div class="max-w-[1600px] mx-auto">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              class="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
              @click="goBack"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1
                :class="[
                  theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                  'text-2xl font-bold'
                ]"
              >
                Edit Report
              </h1>
              <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm']">
                <span v-if="reportData">{{ reportData.name || reportData.id }}</span>
                <span v-else>Loading...</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="max-w-[1600px] mx-auto p-6"
    >
      <LoadingState message="Loading report..." />
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="max-w-[1600px] mx-auto p-6"
    >
      <div
        :class="[
          theme.isDark.value ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200',
          'border rounded-lg p-6 text-center shadow-md-custom'
        ]"
        style="box-shadow: var(--shadow-md)"
      >
        <svg
          class="mx-auto h-12 w-12 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-red-700 dark:text-red-300">{{ error }}</h3>
        <div class="mt-4 flex justify-center space-x-3">
          <button
            class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
            @click="goBack"
          >
            Go Back
          </button>
          <button
            class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
            @click="fetchReport"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-else
      class="max-w-[1600px] mx-auto p-6"
    >
      <ReportForm
        mode="edit"
        :report-data="reportData"
        @saved="handleSaved"
        @cancel="goBack"
      />
    </div>

    <!-- Toast Notification -->
    <Toast
      v-model:show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :duration="4000"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, inject, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import type { ReportConfig } from '../../types/platform-report'
import { platformReportsService } from '../../services/platform-reports'
import ReportForm from './components/ReportForm.vue'
import Toast from '../../components/base/common/Toast.vue'
import LoadingState from '../../components/base/common/LoadingState.vue'

const theme = inject('theme', useTheme())
const router = useRouter()
const route = useRoute()

// State
const reportData = ref<ReportConfig | null>(null)
const isLoading = ref(true)
const error = ref<string | null>(null)

// Toast
const toast = reactive({
  show: false,
  message: '',
  type: 'success' as 'success' | 'error'
})

const showToast = (message: string, type: 'success' | 'error') => {
  toast.message = message
  toast.type = type
  toast.show = true
}

const goBack = () => {
  router.push('/platform-report')
}

const fetchReport = async () => {
  const reportId = route.params.id as string

  if (!reportId) {
    error.value = 'Report ID is required'
    isLoading.value = false
    return
  }

  isLoading.value = true
  error.value = null

  try {
    reportData.value = await platformReportsService.getReportById(reportId)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load report'
  } finally {
    isLoading.value = false
  }
}

const handleSaved = async () => {
  showToast('Report updated successfully', 'success')
  // Refresh the report data to get updated values
  await fetchReport()
}

onMounted(() => {
  fetchReport()
})
</script>
